import * as React from "react";
import { useState, useRef, useEffect } from "react";
import Box from "../box";
import Text from "../text";
import Stack from "../stack";
import { store } from "../../models/store";
import type { NobleTxStepItemProps } from "./noble.types";
import * as styles from "./noble.css";

function NobleTxStepItem(props: NobleTxStepItemProps) {
  const cleanupRef = useRef<() => void>(null);
  const [theme, setTheme] = useState(() => "light");

  function getIconColor() {
    return theme === "light" ? "$gray500" : "$blue500";
  }

  useEffect(() => {
    setTheme(store.getState().theme);
    cleanupRef.current = store.subscribe((newState, prevState) => {
      setTheme(newState.theme);
    });
  }, []);

  useEffect(() => {
    return () => {
      if (typeof cleanupRef.current === "function") cleanupRef.current();
    };
  }, []);

  return (
    <Stack
      space="9px"
      attributes={{
        alignItems: "center",
      }}
    >
      {props.status === "completed" ? (
        <svg
          width="18"
          height="18"
          viewBox="0 0 18 18"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <Box
            as="path"
            fill="$primary"
            attributes={{
              d: "M9 18C11.3871 18 13.6759 17.0519 15.3643 15.3643C17.0519 13.676 18 11.3871 18 9C18 6.61293 17.0519 4.32405 15.3643 2.63567C13.676 0.948111 11.3871 0 9 0C6.61293 0 4.32405 0.948091 2.63567 2.63567C0.948111 4.32399 0 6.61293 0 9C0 11.3871 0.948091 13.6759 2.63567 15.3643C4.32399 17.0519 6.61293 18 9 18ZM4.32778 9.23702C4.64688 8.92022 5.16235 8.92022 5.48145 9.23702L7.36383 11.1194L12.1012 6.38198C12.4257 6.10353 12.9097 6.12272 13.2119 6.42494C13.5142 6.72717 13.5326 7.21042 13.2549 7.53564L7.93683 12.8537H7.93606C7.61696 13.1713 7.10149 13.1713 6.78239 12.8537L4.32777 10.3991C4.17283 10.2457 4.08614 10.0363 4.08614 9.81844C4.08614 9.59982 4.17283 9.39119 4.32778 9.23702Z",
              "stroke-width": "0.2",
            }}
          />
        </svg>
      ) : null}
      {props.status === "processing" ? (
        <Box width="$9" height="$9" className={styles.processingIcon}>
          <svg
            width="22"
            height="22"
            viewBox="0 0 22 22"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <Box
              as="path"
              fill={getIconColor()}
              stroke={getIconColor()}
              attributes={{
                d: "M10.0313 18.5246V20.5009C10.0313 20.8276 10.2985 21.0947 10.6252 21.0947C10.9519 21.0947 11.219 20.8276 11.219 20.5009V18.5246C11.219 18.1979 10.9519 17.9308 10.6252 17.9308C10.2985 17.9308 10.0313 18.1979 10.0313 18.5246Z",
                "stroke-width": "0.2",
              }}
            />
            <Box
              as="path"
              fill={getIconColor()}
              stroke={getIconColor()}
              attributes={{
                d: "M13.1016 18.1519L13.1015 18.1519L13.1021 18.1533L13.8582 19.9814L13.8582 19.9814L13.8588 19.9828C13.9523 20.1996 14.1648 20.3499 14.4055 20.3499C14.4842 20.3499 14.5619 20.3318 14.6316 20.3025C14.7799 20.2432 14.8942 20.1286 14.9534 19.9802C15.012 19.8331 15.0119 19.6733 14.9535 19.5256L14.9535 19.5256L14.9529 19.5241L14.1968 17.6961L14.1968 17.6959C14.071 17.3932 13.7267 17.2504 13.4238 17.3748C13.2754 17.4341 13.1608 17.5487 13.1016 17.6973C13.043 17.8444 13.0431 18.0042 13.1016 18.1519Z",
                "stroke-width": "0.2",
              }}
            />
            <Box
              as="path"
              fill={getIconColor()}
              stroke={getIconColor()}
              attributes={{
                d: "M17.1894 18.0275L17.1895 18.0277C17.3025 18.1406 17.4523 18.2006 17.6114 18.2006C17.7704 18.2006 17.9193 18.1416 18.0333 18.0277L18.0333 18.0277C18.2651 17.7959 18.2651 17.4213 18.0333 17.1895L16.6351 15.7914C16.4034 15.5596 16.0287 15.5596 15.797 15.7914C15.5652 16.0231 15.5652 16.3977 15.7969 16.6295C15.7969 16.6295 15.7969 16.6295 15.797 16.6295L17.1894 18.0275Z",
                "stroke-width": "0.2",
              }}
            />
            <Box
              as="path"
              fill={getIconColor()}
              stroke={getIconColor()}
              attributes={{
                d: "M18.1541 13.0914L18.1539 13.0913L17.6969 14.1907C17.6969 14.1907 17.6969 14.1907 17.697 14.1907L19.523 14.946C19.6019 14.9822 19.6755 14.9942 19.7558 14.9942C19.9986 14.9942 20.2138 14.8517 20.3035 14.6249C20.4274 14.3229 20.2858 13.9725 19.9821 13.8474C19.982 13.8474 19.982 13.8474 19.982 13.8474L18.1541 13.0914Z",
                "stroke-width": "0.2",
              }}
            />
            <Box
              as="path"
              fill={getIconColor()}
              stroke={getIconColor()}
              attributes={{
                d: "M21.1011 10.6208C21.1011 10.2941 20.8339 10.027 20.5072 10.027H18.5309C18.2042 10.027 17.9371 10.2941 17.9371 10.6208C17.9371 10.9475 18.2042 11.2146 18.5309 11.2146H20.5072C20.8339 11.2146 21.1011 10.9475 21.1011 10.6208Z",
                "stroke-width": "0.2",
              }}
            />
            <Box
              as="path"
              fill={getIconColor()}
              stroke={getIconColor()}
              attributes={{
                d: "M3.09435 13.0913L3.09421 13.0914L1.2663 13.8474L1.30438 13.9398L1.26616 13.8474L1.26621 13.8474C0.962236 13.9726 0.820677 14.3235 0.94507 14.6256L0.945068 14.6256L0.945297 14.6261C1.03975 14.8517 1.25887 14.9942 1.49711 14.9942C1.576 14.9942 1.65392 14.976 1.72372 14.9466C1.72383 14.9466 1.72395 14.9465 1.72406 14.9465L3.55132 14.1907L3.51324 14.0983L3.55146 14.1907L3.55137 14.1907C3.85537 14.0655 3.99695 13.7147 3.87255 13.4125C3.74736 13.1085 3.39648 12.9669 3.09435 13.0913Z",
                "stroke-width": "0.2",
              }}
            />
            <Box
              as="path"
              fill={getIconColor()}
              stroke={getIconColor()}
              attributes={{
                d: "M20.302 6.61016L20.302 6.61016L20.3018 6.60956C20.1772 6.31256 19.8333 6.16378 19.5294 6.28893L19.5293 6.28899L17.7014 7.04498L17.7394 7.13745L17.7012 7.04504L17.7013 7.04502C17.3973 7.17024 17.2557 7.52108 17.3801 7.82318L17.4726 7.78511L17.3799 7.82257C17.4692 8.04372 17.6884 8.19179 17.9266 8.19179C18.0055 8.19179 18.0834 8.17363 18.1532 8.14423C18.1533 8.14418 18.1534 8.14413 18.1535 8.14408L19.9808 7.38836L19.9427 7.2959L19.9809 7.3883L19.9809 7.38834C20.2849 7.26313 20.4264 6.91227 20.302 6.61016Z",
                "stroke-width": "0.2",
              }}
            />
            <Box
              as="path"
              fill={getIconColor()}
              stroke={getIconColor()}
              attributes={{
                d: "M4.06205 18.0272L4.06206 18.0272L5.46019 16.629C5.69197 16.3973 5.69197 16.0226 5.46019 15.7909C5.22842 15.5591 4.85379 15.5591 4.62202 15.7909L3.2247 17.1882C2.98657 17.4161 2.98812 17.797 3.21925 18.0281C3.33237 18.1412 3.48137 18.2001 3.64018 18.2001C3.79914 18.2001 3.94809 18.1411 4.06205 18.0272Z",
                "stroke-width": "0.2",
              }}
            />
            <Box
              as="path"
              fill={getIconColor()}
              stroke={getIconColor()}
              attributes={{
                d: "M16.6353 5.45028L16.6354 5.45028L18.0335 4.05214C18.2653 3.82036 18.2652 3.44573 18.0335 3.21397C17.8017 2.98219 17.4215 2.98219 17.1897 3.21397L15.7916 4.6121C15.5598 4.84387 15.5598 5.2185 15.7916 5.45028C15.9045 5.5632 16.0544 5.62318 16.2135 5.62318C16.3724 5.62318 16.5214 5.56422 16.6353 5.45028Z",
                "stroke-width": "0.2",
              }}
            />
            <Box
              as="path"
              fill={getIconColor()}
              stroke={getIconColor()}
              attributes={{
                d: "M6.29354 19.9802C6.35269 20.1286 6.46705 20.2432 6.61533 20.3025C6.68488 20.3318 6.76167 20.3499 6.84137 20.3499C7.0841 20.3499 7.29929 20.2074 7.38902 19.9806L8.14477 18.1533L8.14477 18.1533L8.14525 18.1521C8.20367 18.0055 8.20376 17.8463 8.14594 17.699C8.09114 17.5474 7.97632 17.4338 7.82758 17.3747C7.52429 17.2503 7.18125 17.3937 7.05076 17.6945L7.05074 17.6945L7.05008 17.6961L6.29403 19.5241L6.29402 19.5241L6.29354 19.5253C6.23493 19.6724 6.23503 19.8322 6.29345 19.9799L6.29354 19.9802Z",
                "stroke-width": "0.2",
              }}
            />
            <Box
              as="path"
              fill={getIconColor()}
              stroke={getIconColor()}
              attributes={{
                d: "M14.9583 1.26666L14.9583 1.26665C14.899 1.11792 14.7843 1.0032 14.6356 0.943934C14.4892 0.885627 14.329 0.885002 14.1857 0.944181C14.0374 1.00344 13.9231 1.1178 13.8638 1.26602L13.108 3.0935L13.108 3.0935L13.1075 3.0947C13.0489 3.24179 13.049 3.4016 13.1074 3.54931L13.1075 3.54955C13.1667 3.69798 13.281 3.81252 13.4293 3.8719C13.4988 3.90111 13.5756 3.91922 13.6553 3.91922C13.8981 3.91922 14.1133 3.77674 14.203 3.55L14.9587 1.7227L14.9588 1.72271L14.9595 1.72065C15.0167 1.57329 15.0169 1.41373 14.9583 1.26666ZM13.2004 3.51253C13.2495 3.63576 13.344 3.73027 13.4672 3.77937L13.2004 3.13172C13.1513 3.25495 13.1513 3.38836 13.2004 3.51253Z",
                "stroke-width": "0.2",
              }}
            />
          </svg>
        </Box>
      ) : null}
      {props.status === "pending" ? <Box width="$9" height="$9" /> : null}
      <Text
        color="$textSecondary"
        fontSize="$xs"
        fontWeight="$normal"
        lineHeight="$base"
      >
        {props.step}
      </Text>
    </Stack>
  );
}

export default NobleTxStepItem;
